import * as Sentry from '@sentry/vue'
import axios from 'axios'
import { defineStore } from 'pinia'
import posthog from 'posthog-js'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  applyAdminSession,
  discardAdminSession,
  getStoredAdminSession,
  resolveAdminAccessToken,
} from '@/composables/useAuthentication'
import { CustomerService } from '@/services/admin'
import { AdminAuthService } from '@/services/admin-auth'
import { useCustomerStore } from '@/stores/customer-store'
import { useGolfStore } from '@/stores/golf-store'
import { useUiStore } from '@/stores/ui-store'
import type { AuthAdminUpdateMe, IAdminTokens, ICustomerUser, IUserInformation } from '@/types'

/** Consent-gated PostHog storage: device storage only after the cookie banner is accepted. */
export function consentPersistence(
  preference: 'accepted' | 'declined' | null,
): 'localStorage+cookie' | 'memory' {
  return preference === 'accepted' ? 'localStorage+cookie' : 'memory'
}

// Superadmin activity and internal team traffic carry no product signal — excluded from tracking
// entirely by opting the whole session out at the identify step.
const INTERNAL_EMAIL_DOMAIN = '@pinvision.it'

export const useUserStore = defineStore(
  'user-store',
  () => {
    const router = useRouter()
    const isAuthenticated = ref(false)
    const user = ref<IUserInformation | null>(null)
    const cookiePreference = ref<'accepted' | 'declined' | null>(null)

    function identifyUser() {
      if (!user.value) {
        return
      }

      const isExcluded =
        user.value.isSuperAdmin || user.value.email?.endsWith(INTERNAL_EMAIL_DOMAIN)
      if (isExcluded) {
        posthog.opt_out_capturing()
        return
      }
      if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing()
      }

      posthog.identify(user.value.id.toString(), {
        email: user.value.email,
        name:
          [user.value.given_name, user.value.family_name].filter(Boolean).join(' ') || undefined,
        role: user.value.isSuperAdmin ? 'superadmin' : 'admin',
      })
    }

    // Map the customers_user profile returned by the API onto the store's IUserInformation shape.
    // `id_golfclub_admin` and `isSuperAdmin` are kept for backwards compatibility with the many
    // components that already read them.
    function applyProfile(me: ICustomerUser) {
      user.value = {
        ...(user.value ?? {}),
        id: me.id,
        email: me.email,
        given_name: me.given_name,
        family_name: me.family_name,
        id_golfclub_admin: me.id_golfclub ?? undefined,
        id_golfclub_list: me.id_golfclub_list ?? [],
        isSuperAdmin: me.role === 'superadmin',
      } as IUserInformation
    }

    // Drop the auth state built up during a login attempt without touching unrelated persisted
    // state (locale, cookie preference, other pinia stores).
    function discardSession() {
      isAuthenticated.value = false
      user.value = null
      discardAdminSession()
    }

    // Shared tail of every login flow (password, SSO): persist the tokens, load the profile and
    // club data, mark the session authenticated and land on the dashboard.
    async function bootstrapSession(tokens: IAdminTokens) {
      const session = applyAdminSession(tokens)

      const me = await AdminAuthService.me(session.accessToken)
      // The backend refuses to issue tokens for non-admin roles; this mirrors that gate should a
      // stale session or older API ever slip one through.
      if (me.role !== 'admin' && me.role !== 'superadmin') {
        discardSession()
        throw new Error('Unauthorized user')
      }
      applyProfile(me)

      if (me.id_golfclub) {
        localStorage.setItem('gc', me.id_golfclub.toString())
      }

      // Superadmins land with the superadmin view on; the toggle then persists via ui-store.
      const uiStore = useUiStore()
      uiStore.toggleSuperAdminMenu(me.role === 'superadmin')

      // Load customer and golf club info for the club this admin manages.
      const customerStore = useCustomerStore()
      const golfStore = useGolfStore()
      if (me.id_golfclub) {
        await customerStore.setCustomerInfo(me.id_golfclub)
        await golfStore.setGolfClubInfo(me.id_golfclub)
      }

      isAuthenticated.value = true
      identifyUser()
      posthog.capture('user_signed_in', {
        user_role: user.value?.isSuperAdmin ? 'superadmin' : 'admin',
      })
      try {
        await router.push({ name: 'dashboard' })
      } catch (navErr) {
        console.error('Navigation failed:', navErr)
      }
    }

    async function signIn(email: string, password: string): Promise<void> {
      try {
        const tokens = await AdminAuthService.login(email, password)
        await bootstrapSession(tokens)
      } catch (err) {
        console.error('Sign in error:', err)
        posthog.capture('sign_in_failed', {
          method: 'password',
          status_code: axios.isAxiosError(err) ? err.response?.status : undefined,
        })
        discardSession()
        throw err // rethrow for UI feedback
      }
    }

    /** Logs in through the partner SSO flow: the apikey is single-use, so a failed bootstrap
     * cannot be retried with the same key — the partner exchange must be restarted. */
    async function signInWithSso(apikey: string): Promise<void> {
      try {
        const tokens = await AdminAuthService.sso(apikey)
        await bootstrapSession(tokens)
      } catch (err) {
        console.error('SSO sign in error:', err)
        posthog.capture('sign_in_failed', {
          method: 'sso',
          status_code: axios.isAxiosError(err) ? err.response?.status : undefined,
        })
        discardSession()
        throw err // rethrow for UI feedback
      }
    }

    async function signOut(redirect: boolean = false) {
      // Refresh first if needed so the server-side revocation actually happens; with a stale
      // access token the logout call would 401 and leave the refresh token alive.
      const accessToken = await resolveAdminAccessToken()
      const session = getStoredAdminSession()
      if (accessToken && session) {
        try {
          await AdminAuthService.logout(accessToken, session.refreshToken)
        } catch (err) {
          console.error('Admin logout failed', err)
        }
      }

      isAuthenticated.value = false
      user.value = null
      discardAdminSession()
      posthog.capture('user_signed_out')
      posthog.reset()
      localStorage.clear() // also removes the persisted admin session and gc keys
      Sentry.setUser(null)

      if (redirect) {
        try {
          await router.push({ name: 'home' })
        } catch (navErr) {
          console.error('Navigation failed:', navErr)
        }
      }
    }

    /**
     * Switches the active club. Every club switch in the app funnels through here.
     *
     * The server authorizes the switch (403 outside the user's own id_golfclub_list, superadmins
     * excepted) and hands back tokens whose `custom:gcs` carries the new club — without swapping them
     * in, RolesGuard would keep rejecting the `x-gc` header for the rest of the token TTL.
     *
     * @param clubId - The ID of the club to switch to.
     */
    async function changeClub(clubId: number) {
      const accessToken = await resolveAdminAccessToken()
      if (!accessToken) {
        throw new Error('Not authenticated')
      }

      // customers_user.id_golfclub is a FK to customer.id, and new or just-live clubs have no
      // customer row yet — create it on first switch rather than hiding those clubs.
      const customer = await CustomerService.getSingle(clubId).catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      })
      if (!customer) {
        await CustomerService.create({ id: clubId })
      }

      applyAdminSession(await AdminAuthService.switchClub(accessToken, clubId))
      localStorage.setItem('gc', clubId.toString())
      user.value!.id_golfclub_admin = clubId

      await useCustomerStore().setCustomerInfo(clubId)
      await useGolfStore().setGolfClubInfo(clubId)
      posthog.capture('club_changed', { golfclub_id: clubId })
    }

    async function refreshUser() {
      const accessToken = await resolveAdminAccessToken()
      if (!accessToken) {
        return
      }
      try {
        const me = await AdminAuthService.me(accessToken)
        applyProfile(me)
        identifyUser()
      } catch (error) {
        console.error('Error refreshing user:', error)
        // A 401 here means the session was revoked server-side (e.g. the admin was
        // deactivated) — log out directly instead of relying on the redirect-based
        // cleanup in the axios interceptor to be noticed on the next login mount.
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          await signOut()
        }
      }
    }

    /** Updates the current admin user's profile (given/family name) on customers_user. */
    async function updateProfile(data: AuthAdminUpdateMe) {
      const accessToken = await resolveAdminAccessToken()
      if (!accessToken) {
        throw new Error('Not authenticated')
      }
      const me = await AdminAuthService.updateMe(accessToken, data)
      applyProfile(me)
    }

    function setCookiePreference(preference: 'accepted' | 'declined') {
      cookiePreference.value = preference
      // set_config, unlike capture/identify/reset, has no built-in uninitialized guard
      if (posthog.__loaded) {
        posthog.set_config({ persistence: consentPersistence(preference) })
      }
      posthog.capture('cookie_consent_updated', { preference })
    }

    return {
      user,
      isAuthenticated,
      cookiePreference,
      identifyUser,
      signIn,
      signInWithSso,
      signOut,
      changeClub,
      refreshUser,
      updateProfile,
      setCookiePreference,
    }
  },
  {
    persist: true,
  },
)
